/**
 * Client-side input checks for institutional sign-in and linking. These are
 * UX guards only: the server re-validates the address and owns the proof (an
 * OTP sent to it). Nothing here decides who owns an account.
 */
import { isEmailCodeAnyAddressEnabled } from "./institutional-flags";

// RFC 5321 caps a forward path at 254 characters.
export const MAX_EMAIL_LENGTH = 254;
export const INSTITUTION_MIN_LENGTH = 2;
export const INSTITUTION_MAX_LENGTH = 120;
export const DISPLAY_NAME_MIN_LENGTH = 2;
export const DISPLAY_NAME_MAX_LENGTH = 80;

// Pragmatic address shape: a dot-atom local part, then one or more DNS labels
// ending in .edu or .org (subdomains such as med.school.edu are fine). Quoted
// local parts and IP literals are deliberately rejected — no institution
// issues them, and the server applies the same .edu/.org rule.
const INSTITUTIONAL_EMAIL_PATTERN =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:edu|org)$/;

/** Trimmed, lower-cased address — the form sent to the server. */
export const normalizeInstitutionalEmail = (value) =>
  (typeof value === "string" ? value.trim().toLowerCase() : "");

export const isInstitutionalEmail = (value) => {
  const email = normalizeInstitutionalEmail(value);
  return email.length > 0
    && email.length <= MAX_EMAIL_LENGTH
    && INSTITUTIONAL_EMAIL_PATTERN.test(email);
};

/** Collapse internal whitespace so "  Mayo   Clinic " is stored as "Mayo Clinic". */
export const normalizeFreeText = (value) =>
  (typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "");

/**
 * Returns a user-facing error string, or null when the address is acceptable.
 * Distinguishes "not an email" from "not .edu/.org" so a student who typed a
 * Gmail address learns to use Google sign-in instead.
 */
export const validateInstitutionalEmail = (value) => {
  const email = normalizeInstitutionalEmail(value);
  if (!email) return "Enter your institutional email address.";
  if (email.length > MAX_EMAIL_LENGTH || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return "Enter a valid email address.";
  }
  if (!isInstitutionalEmail(email)) {
    return "Use an institutional email ending in .edu or .org. For a personal address, use Google or phone sign-in.";
  }
  return null;
};

// The same dot-atom local part as above under any domain: DNS labels and a letters-only
// top-level label, as the server's own shape check (InstitutionalEmailPolicy).
const ANY_EMAIL_PATTERN =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * The sign-in form's address check. With the any-address flag off it is exactly
 * validateInstitutionalEmail; on, any well-formed address passes (the server refuses
 * staff domains itself).
 */
export const validateLoginEmail = (value) => {
  if (!isEmailCodeAnyAddressEnabled()) return validateInstitutionalEmail(value);
  const email = normalizeInstitutionalEmail(value);
  if (!email) return "Enter your email address.";
  if (email.length > MAX_EMAIL_LENGTH || !ANY_EMAIL_PATTERN.test(email)) {
    return "Enter a valid email address.";
  }
  return null;
};

/**
 * The analytics door for a verified sign-in: "email_code" for a non-school address
 * while the any-address flag is on, otherwise null (the host keeps its own default).
 */
export const emailCodeDoorFor = (email) =>
  (isEmailCodeAnyAddressEnabled() && !isInstitutionalEmail(email) ? "email_code" : null);

export const validateInstitutionName = (value) => {
  const name = normalizeFreeText(value);
  if (!name) return "Enter your school, program or institution.";
  if (name.length < INSTITUTION_MIN_LENGTH) return "Institution name is too short.";
  if (name.length > INSTITUTION_MAX_LENGTH) {
    return `Institution name must be ${INSTITUTION_MAX_LENGTH} characters or fewer.`;
  }
  return null;
};

export const validateDisplayName = (value) => {
  const name = normalizeFreeText(value);
  if (!name) return "Enter your name.";
  if (name.length < DISPLAY_NAME_MIN_LENGTH) return "Name is too short.";
  if (name.length > DISPLAY_NAME_MAX_LENGTH) {
    return `Name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer.`;
  }
  return null;
};

// Server codes are numeric; accept 4–8 digits so a provider change to the code
// length does not need a client release. Spaces/dashes from a paste are dropped.
export const normalizeOtp = (value) =>
  (typeof value === "string" ? value.replace(/[\s-]/g, "") : "");

export const isPlausibleOtp = (value) => /^\d{4,8}$/.test(normalizeOtp(value));
